"use client";

import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";

interface ProductGalleryProps {
  images: string[];
  productName: string;
}

export default function ProductGallery({ images, productName }: ProductGalleryProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const scrollTo = useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api]
  );

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  return (
    <div className="flex flex-row gap-3 xl:gap-10 w-full xl:w-[700px] flex-shrink-0">

      {/* Thumbnails */}
      <div className="flex flex-col gap-2 xl:w-[70px] xl:h-[382px]">
        {images.map((img, i) => (
          <button
            key={i}
            onClick={() => scrollTo(i)}
            className={`relative w-[50px] h-[50px] sm:w-[55px] sm:h-[55px] md:w-[60px] md:h-[60px] xl:w-[70px] xl:h-[70px] rounded-[6px] overflow-hidden bg-background-50 cursor-pointer flex-shrink-0 transition-all ${
              current === i ? "ring-1 ring-neutral-500" : ""
            }`}
          >
            <Image src={img} alt="" fill className="object-contain" />
          </button>
        ))}
      </div>

      {/* Main Image */}
      <div className="w-full h-[250px] sm:h-[350px] md:h-[450px] lg:h-[550px] xl:w-[630px] xl:h-[629px] bg-background-50 rounded-[12px] overflow-hidden relative">
        <Carousel setApi={setApi} opts={{ loop: true }} className="w-full h-full">
          <CarouselContent>
            {images.map((img, i) => (
              <CarouselItem key={i}>
                <div className="aspect-square rounded-xl sm:rounded-2xl overflow-hidden relative">
                  <Image
                    src={img}
                    alt={`${productName} - Image ${i + 1}`}
                    fill
                    className="object-contain p-6 xl:p-8"
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          {/* Arrows */}
          <button
            onClick={scrollPrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
          >
            <ChevronLeft className="h-6 w-6 xl:h-8 xl:w-8 text-[#7D8082]" />
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
          >
            <ChevronRight className="h-6 w-6 xl:h-8 xl:w-8 text-[#7D8082]" />
          </button>

        </Carousel>
      </div>

    </div>
  );
}